import * as React from 'react';

import { translate } from '~l10n/client';
import { useSetAtom } from 'jotai';

import { AddQuizModalEventAdapter } from '~analytics/adapters/AddQuizModalEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { SmallButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { xAgoFormatter } from '~shared/components/helpers';
import { IconDocument, IconFolder } from '~shared/components/icons/standard';
import { Search } from '~shared/components/Table/update/components/Search';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import {
  AddQuizDialog,
  useAddQuizDialog,
} from '~teacher/components/AddQuizDialog';
import {
  PaywallAction,
  usePaywallReducer,
} from '~teacher/components/PaywallDialog';
import { FolderNotFound } from '~teacher/components/quizzes/FolderNotFound';
import { MergeQuizDialog } from '~teacher/components/quizzes/MergeQuizDialog/MergeQuizDialog';
import { useMergeQuizDialog } from '~teacher/components/quizzes/MergeQuizDialog/useMergeQuizDialog';
import { SharedLibraryPaywall } from '~teacher/components/SharedLibraryPaywall';
import { CopyQuizToWorkspaceDialog } from '~teacher/components/workspaces/CopyQuizToWorkspace';
import { commonDateSorter } from '~teacher/components/workspaces/DialogQuizItemList/DialogQuizItemList';
import { DuplicateWorkspaceQuizDialog } from '~teacher/components/workspaces/DuplicateWorkspaceQuizDialog';
import { ImportQuizToWorkspaceDialog } from '~teacher/components/workspaces/ImportQuizToWorkspace';
import { WorkspaceInviteDialog } from '~teacher/components/workspaces/InviteWorkspaceDialog/InviteWorkspaceDialog';
import { useMoveWorkspaceItemsDialog } from '~teacher/components/workspaces/MoveWorkspaceItemsDialog/useMoveWorkspaceItemsDialog';
import {
  RenameWorkspaceFolderDialog,
  useRenameWorkspaceFolderDialog,
} from '~teacher/components/workspaces/RenameWorkspaceFolderDialog';
import { closeRenameFolder } from '~teacher/components/workspaces/RenameWorkspaceFolderDialog/useRenameWorkspaceFolderDialog';
import { CreateWorkspaceFolderDialog } from '~teacher/components/workspaces/WorkspaceFolderDialog/CreateWorkspaceFolderDialog';
import { useCreateWorkspaceFolderDialog } from '~teacher/components/workspaces/WorkspaceFolderDialog/useCreateWorkspaceFolderDialog';
import { useCopyToAction } from '~teacher/components/workspaces/WorkspaceQuizzes/actions';
import { useCreateQuiz } from '~teacher/components/workspaces/WorkspaceQuizzes/hooks/useCreateQuiz';
import { useHandleWorkspaceItemSelection } from '~teacher/components/workspaces/WorkspaceQuizzes/hooks/useHandleWorkspaceItemSelection';
import { useImportQuizToWorkspaceDialog } from '~teacher/components/workspaces/WorkspaceQuizzes/hooks/useImportQuizToWorkspaceDialog';
import {
  WorkspaceItemTable,
  useParentIdFilter,
} from '~teacher/components/workspaces/WorkspaceQuizzes/WorkspaceItemTable';
import { WorkspaceQuizzesBreadcrumbs } from '~teacher/components/workspaces/WorkspaceQuizzes/WorkspaceQuizzesBreadcrumbs';
import { WorkspaceQuizzesEmptyState } from '~teacher/components/workspaces/WorkspaceQuizzes/WorkspaceQuizzesEmptyState';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import {
  useGetWorkspaceDetail,
  useWorkspacePermissionsV2,
} from '~teacher/hook/workspaces';
import { useWorkspaceFolders } from '~teacher/hook/workspaces/useWorkspaceFolders';
import {
  WorkspaceItem,
  useWorkspaceItems,
} from '~teacher/hook/workspaces/useWorkspaceItems';
import { isLaunchWorkspaceQuizDialogOpenAtom } from '~teacher/launchv2/atoms';
import {
  ConfirmStopRunningActivity,
  useConfirmStopRunningActivity,
} from '~teacher/launchv2/ConfirmLaunchQuiz';
import { LaunchWorkspaceQuizDialog } from '~teacher/launchv2/LaunchWorkspaceQuizDialog';
import { proCheck } from '~teacher/util/proCheck';
import { Dialog } from '~shared/components/Dialog/Dialog';
import { QuizAnswerKeyExportModal } from '~teacher/components/quizzes/QuizListItem/QuizAnswerKeyExportModal';

import { WorkspaceRole } from '../../../../api/workspaces/getWorkspaceMembers';
import { MoveWorkspaceItemsDialog } from '../MoveWorkspaceItemsDialog/MoveWorkspaceItemsDialog';
import {
  useCopyQuizToPersonalAction,
  useDeleteWorkspaceItemAction,
  useDownloadQuizAction,
  useExportAnswerKeyAction,
  useDuplicateQuizAction,
  useLaunchQuizAction,
  useMergeWorkspaceQuizAction,
  useMoveWorkspaceItemAction,
  useRenameFolderAction,
} from './actions';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const activity = require('../../../../../../../shared/models/activity');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const room = require('../../../../../../../shared/models/room');

interface Props {
  workspaceId: string;
  itemId?: string;
}

const columns = [
  {
    id: 'type',
    accessor: (item) => item.type,
    customWidth: '60px',
    disableSortBy: true,
    Cell: ({ value }: { value: any }) =>
      value === 'quiz' ? <IconDocument /> : <IconFolder />,
  },
  {
    Header: translate('Name'),
    id: 'name',
    accessor: (item) => item.name,
    sortBy: 'alphanumeric',
  },
  {
    Header: translate('Modified'),
    id: 'modified',
    customWidth: '160px',
    sortType: commonDateSorter,
    accessor: (workspaceItem: WorkspaceItem) =>
      workspaceItem.type === 'quiz'
        ? xAgoFormatter({ date: workspaceItem.publishedDate })
        : xAgoFormatter({ date: workspaceItem.createdDate }),
  },
];

export function WorkspaceQuizzes({ workspaceId, itemId }: Props) {
  const isWorkspaceIdExist = itemId !== null && itemId !== undefined;

  const workspaceFolders = useWorkspaceFolders({
    workspaceId,
    isDeleted: true,
    enabled: itemId !== null && itemId !== undefined,
  });

  const deletedWorkspaceFolders = useWorkspaceFolders({
    workspaceId,
    isDeleted: false,
    enabled: itemId !== null && itemId !== undefined,
  });

  const currentFolder = React.useMemo(() => {
    return isWorkspaceIdExist
      ? [
          ...(workspaceFolders.data ?? []),
          ...(deletedWorkspaceFolders.data ?? []),
        ].filter((folder) => folder.id === itemId)[0]
      : null;
  }, [
    isWorkspaceIdExist,
    workspaceFolders.data,
    deletedWorkspaceFolders.data,
    itemId,
  ]);

  if (isWorkspaceIdExist && !currentFolder) {
    return <FolderNotFound />;
  }

  return <WorkspaceQuizzesContent workspaceId={workspaceId} itemId={itemId} />;
}

function useOpenCreateFolderDialog(
  featurePaywallDispatch: (value: PaywallAction) => void
) {
  const hasPaidPlan = proCheck.hasPaidPlan();

  const {
    close: closeCreateFolder,
    open,
    isOpen: isCreateFolderOpen,
  } = useCreateWorkspaceFolderDialog({
    canCreateFolder: true,
  });

  const openCreateFolder = () => {
    if (!hasPaidPlan) {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListAddFolder],
      });
      return;
    }

    open();
  };
  return {
    closeCreateFolder,
    isCreateFolderOpen,
    openCreateFolder: openCreateFolder,
  };
}

function useOpenQuizEditor({
  parentFolderId,
  workspaceId,
  featurePaywallDispatch,
}: {
  parentFolderId: string;
  workspaceId: string;
  featurePaywallDispatch: (value: PaywallAction) => void;
}) {
  const { createQuiz, canCreateQuiz } = useCreateQuiz({
    workspaceId,
    parentFolderId,
  });

  return (options?: { aiFeature?: 'generate' | 'import' }) => {
    if (canCreateQuiz) {
      createQuiz(options);
    } else {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListAddQuiz],
      });
    }
  };
}

function useAddWorkspaceQuizDialog({
  workspaceId,
  featurePaywallDispatch,
}: {
  workspaceId: string;
  featurePaywallDispatch: (value: PaywallAction) => void;
}) {
  const {
    permissions: { createQuiz: canCreateQuiz },
  } = useWorkspacePermissionsV2({ workspaceId });

  const { openAddQuizDialog, closeAddQuizDialog, isAddQuizDialogOpen } =
    useAddQuizDialog();

  return {
    openAddQuizDialog: () => {
      if (canCreateQuiz) {
        new AddQuizModalEventAdapter({
          source: 'shared_library',
          action: 'open',
        }).sendAnalytics();

        openAddQuizDialog();
      } else {
        featurePaywallDispatch({
          type: 'OPEN',
          touch: [PaywallScreens.SharedLibraryListAddQuiz],
        });
      }
    },
    closeAddQuizDialog,
    isAddQuizDialogOpen,
  };
}
export function WorkspaceQuizzesContent({ workspaceId, itemId }: Props) {
  const router = useSocrativeRouter();

  const { roleQuery, accountQuery } = useWorkspacePermissionsV2({
    workspaceId,
  });
  const isViewerRole = roleQuery.data === WorkspaceRole.Viewer;

  const isLoading = [roleQuery, accountQuery].some((query) => query.isLoading);

  const [
    { open: featurePaywallOpen, touch: currentPaywallTouch },
    featurePaywallDispatch,
  ] = usePaywallReducer();
  const workspaceItems = useWorkspaceItems({ workspaceId });
  const workspaceInfo = useGetWorkspaceDetail({
    workspaceId: workspaceId,
  });

  const deleteWorkspaceItemAction = useDeleteWorkspaceItemAction({
    workspaceId: workspaceId,
  });

  const copyQuizToPersonalAction = useCopyQuizToPersonalAction(workspaceId);

  const [
    { isOpen: isMoveDialogOpen, workspaceItems: workspaceItemsToMove },
    dispatchMoveWorkspaceItemsDialog,
  ] = useMoveWorkspaceItemsDialog();

  const moveFolderAction = useMoveWorkspaceItemAction({
    dispatchMoveWorkspaceItemsDialog: dispatchMoveWorkspaceItemsDialog,
    workspaceId: workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListMoveItems],
      });
    },
  });

  const [renameFolderState, renameFolderDialogDispatch] =
    useRenameWorkspaceFolderDialog();
  const onCloseRename = () => renameFolderDialogDispatch(closeRenameFolder());

  const renameFolderAction = useRenameFolderAction({
    renameFolderDialogDispatch: renameFolderDialogDispatch,
    workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListRenameFolder],
      });
    },
  });

  const [{ quizzesToMerge, onMergeQuiz, isOpen }, dispatchMergeQuizDialog] =
    useMergeQuizDialog();
  const mergeQuizAction = useMergeWorkspaceQuizAction(
    dispatchMergeQuizDialog,
    workspaceId
  );

  const setLaunchWorkspaceQuizIsOpen = useSetAtom(
    isLaunchWorkspaceQuizDialogOpenAtom
  );

  const [confirmStopRunningActivity, dispatchConfirmStopRunningActivity] =
    useConfirmStopRunningActivity();

  const launchQuizAction = useLaunchQuizAction({
    dispatchConfirmStopRunningActivity: dispatchConfirmStopRunningActivity,
    setLaunchWorkspaceQuizIsOpen: setLaunchWorkspaceQuizIsOpen,
    activityIsLive: activity.isLive(),
    workspaceId: workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryLaunchQuiz],
      });
    },
  });

  const {
    duplicateQuizAction,
    duplicateWorkspaceQuizDialogState,
    dispatchCopyDialog,
  } = useDuplicateQuizAction({
    workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListDuplicateQuiz],
      });
    },
  });

  // downloading a workspace quiz
  const { downloadQuizAction } = useDownloadQuizAction({
    workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryListDownloadQuiz],
      });
    },
  });

  /* 
  Exporting answer key for a workspace quiz
  */
  const {
    exportAnswerKeyAction,
    isAnswerKeyDialogOpen,
    setIsAnswerKeyDialogOpen,
    selectedQuizId,
    selectedWorkspaceId,
  } = useExportAnswerKeyAction({
    workspaceId,
    paywallCallback: () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.SharedLibraryDownloadAnswerKey],
      });
    },
  });

  const [isExportingAnswerKey, setIsExportingAnswerKey] = React.useState(false);

  const handleCloseAnswerKeyDialog = React.useCallback(() => {
    setIsAnswerKeyDialogOpen(false);
    // Reset exporting state when dialog closes to allow reopening
    setIsExportingAnswerKey(false);
  }, [setIsAnswerKeyDialogOpen]);

  const applicationContext = useTeacherApplicationContext();

  const {
    isCopyQuizDialogOpen,
    setIsCopyQuizDialogOpen,
    quizIdsToCopy,
    copyToAction,
  } = useCopyToAction(workspaceId);
  // define row actions
  const actions: Array<Action<WorkspaceItem>> = React.useMemo(() => {
    // Order matters
    const allActions = [
      renameFolderAction,
      launchQuizAction,
      duplicateQuizAction,
      mergeQuizAction,
      copyQuizToPersonalAction,
      copyToAction,
      moveFolderAction,
      downloadQuizAction,
      exportAnswerKeyAction,
      deleteWorkspaceItemAction,
    ];

    // Filter out launchQuizAction for Showbie context
    return applicationContext === TeacherApplicationContext.ShowbieAssessments
      ? allActions.filter((action) => action !== launchQuizAction)
      : allActions;
  }, [
    launchQuizAction,
    copyToAction,
    renameFolderAction,
    moveFolderAction,
    copyQuizToPersonalAction,
    deleteWorkspaceItemAction,
    mergeQuizAction,
    duplicateQuizAction,
    downloadQuizAction,
    exportAnswerKeyAction,
    applicationContext,
  ]);

  const [parentId, setParentId] = React.useState<null | string>(null);
  React.useEffect(() => {
    setParentId(itemId);
  }, [itemId]);

  const [search, setSearch] = React.useState('');

  // parentId filter for folder traversal
  const parentIdFilter = useParentIdFilter({
    search: search,
    parentId: parentId,
  });

  // when selection and row
  const handleSelection = useHandleWorkspaceItemSelection({
    search: search,
    setSearch: setSearch,
    setParentId: setParentId,
    router: router,
  });

  const { importQuizToWorkspaceDialogState, openImportQuiz } =
    useImportQuizToWorkspaceDialog({
      workspaceId: workspaceId,
      featurePaywallDispatch: featurePaywallDispatch,
    });

  const openQuizEditor = useOpenQuizEditor({
    parentFolderId: itemId,
    workspaceId: workspaceId,
    featurePaywallDispatch: featurePaywallDispatch,
  });

  const { closeCreateFolder, openCreateFolder, isCreateFolderOpen } =
    useOpenCreateFolderDialog(featurePaywallDispatch);

  const { openAddQuizDialog, closeAddQuizDialog, isAddQuizDialogOpen } =
    useAddWorkspaceQuizDialog({
      workspaceId: workspaceId,
      featurePaywallDispatch: featurePaywallDispatch,
    });

  const data = React.useMemo(() => workspaceItems.data, [workspaceItems]);
  const loading = React.useMemo(
    () => workspaceItems.workspaceQueries.some((query) => query.isLoading),
    [workspaceItems]
  );
  const [showInviteDialog, setShowInviteDialog] = React.useState(false);

  const handleImportQuiz = () => {
    new AddQuizModalEventAdapter({
      source: 'shared_library',
      action: 'close',
      close_action: 'import',
    }).sendAnalytics();

    openImportQuiz();
    closeAddQuizDialog();
  };

  const onClose = () => {
    new AddQuizModalEventAdapter({
      source: 'shared_library',
      action: 'close',
      close_action: 'close_button',
    }).sendAnalytics();

    closeAddQuizDialog();
  };

  const handleGenerateQuestions = () => {
    new AddQuizModalEventAdapter({
      source: 'shared_library',
      action: 'close',
      close_action: 'ai',
    }).sendAnalytics();

    openQuizEditor({ aiFeature: 'generate' });
    closeAddQuizDialog();
  };

  const handleImportFromText = () => {
    new AddQuizModalEventAdapter({
      source: 'shared_library',
      action: 'close',
      close_action: 'ai-import',
    }).sendAnalytics();

    openQuizEditor({ aiFeature: 'import' });
    closeAddQuizDialog();
  };

  const handleCreateBlank = () => {
    new AddQuizModalEventAdapter({
      source: 'shared_library',
      action: 'close',
      close_action: 'blank',
    }).sendAnalytics();
    openQuizEditor();
    closeAddQuizDialog();
  };

  const roomId = room.id;
  const currentItem = data.find((t) => t.id === itemId);

  return (
    <div className={'relative'}>
      <WorkspaceItemTable
        actions={actions}
        onSelection={handleSelection}
        filters={[parentIdFilter]}
        data={data ?? ([] as WorkspaceItem[])}
        columns={columns}
        enableBulkActions={!isViewerRole}
        emptyState={(resolvedData) => (
          <WorkspaceQuizzesEmptyState
            workspaceId={workspaceId}
            isViewerRole={isViewerRole}
            loading={loading}
            data={resolvedData.length}
            createFolder={openCreateFolder}
            openAddQuizDialog={openAddQuizDialog}
            searchQuery={search}
          />
        )}
        searchQuery={{ id: 'name', value: search }}
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        initialSort={{ id: 'modified', desc: true }}
        workspaceId={workspaceId}
      >
        <>
          <Container fillWidth row margin={'18px 0 0 0'} className={'py-1'}>
            <WorkspaceQuizzesBreadcrumbs
              currentItem={currentItem}
              workspaceId={workspaceId}
            />
          </Container>
          <Container
            fillWidth
            row
            className={'relative'}
            style={{ margin: '3px 0 0 0' }}
          >
            {isLoading ? (
              // this can be left out and just render nothing until the tabs are ready
              <div
                className={
                  'h-full w-full skeleton-loader absolute top-0 left-0 z-10 rounded-2'
                }
              />
            ) : (
              <></>
            )}
            <Container width={2} verticalCenter>
              <Container>
                <Search
                  setQuery={setSearch}
                  name={workspaceInfo.data?.name || ''}
                />
              </Container>
            </Container>
            <Container row margin={'0 0 0 10px'} className={'flex'}>
              {isViewerRole ? null : (
                <SmallButton
                  className={'mr-2 whitespace-no-wrap'}
                  onClick={openCreateFolder}
                >
                  {translate('New Folder')}
                </SmallButton>
              )}
              <SmallButton
                appearance={'primary'}
                className={'mr-2 whitespace-no-wrap'}
                onClick={() => setShowInviteDialog(true)}
              >
                {translate('Invite')}
              </SmallButton>
              {isViewerRole ? null : (
                <SmallButton
                  appearance={'primary'}
                  className="whitespace-no-wrap"
                  onClick={openAddQuizDialog}
                >
                  {translate('Add Quiz')}
                </SmallButton>
              )}
            </Container>
          </Container>
        </>
      </WorkspaceItemTable>
      <CreateWorkspaceFolderDialog
        onClose={closeCreateFolder}
        parentId={parentId}
        open={isCreateFolderOpen}
        workspaceId={workspaceId}
      />
      <DuplicateWorkspaceQuizDialog
        open={duplicateWorkspaceQuizDialogState.open}
        initialQuizName={duplicateWorkspaceQuizDialogState.initialQuizName}
        workspaceQuizId={duplicateWorkspaceQuizDialogState.workspaceQuizId}
        workspaceId={duplicateWorkspaceQuizDialogState.workspaceId}
        onClose={() =>
          dispatchCopyDialog({
            type: 'close',
          })
        }
      />
      <RenameWorkspaceFolderDialog
        workspaceId={renameFolderState.workspaceId}
        workspaceFolderId={renameFolderState.workspaceFolderId}
        isOpen={renameFolderState.isOpen}
        initialFolderName={renameFolderState.initialWorkspaceFolderName}
        onClose={onCloseRename}
      />
      <WorkspaceInviteDialog
        workspaceId={workspaceId}
        open={showInviteDialog}
        setOpen={setShowInviteDialog}
      />
      <MoveWorkspaceItemsDialog
        workspaceId={workspaceId}
        isOpen={isMoveDialogOpen}
        onClose={() => {
          dispatchMoveWorkspaceItemsDialog({ type: 'close' });
        }}
        workspaceItems={workspaceItemsToMove}
        workspaceName={workspaceInfo.data?.name}
        parentId={parentId}
        router={router}
      />
      <SharedLibraryPaywall
        open={featurePaywallOpen}
        onClose={() => featurePaywallDispatch({ type: 'CLOSE' })}
        paywallScreen={currentPaywallTouch ? currentPaywallTouch[0] : null}
      />
      <MergeQuizDialog
        onClose={() => dispatchMergeQuizDialog({ type: 'CLOSE' })}
        isOpen={isOpen}
        quizzesToMerge={quizzesToMerge}
        onMergeQuiz={onMergeQuiz}
      />
      <CopyQuizToWorkspaceDialog
        isOpen={isCopyQuizDialogOpen}
        onClose={() => setIsCopyQuizDialogOpen(false)}
        itemIdsToMove={quizIdsToCopy}
        sourceWorkspace={workspaceId}
      />
      <ConfirmStopRunningActivity
        activityInstanceId={activity.getId()}
        isOpen={confirmStopRunningActivity.isOpen}
        onClose={confirmStopRunningActivity.onClose}
        openLaunchQuizDialog={confirmStopRunningActivity.openLaunchQuizDialog}
      />
      <LaunchWorkspaceQuizDialog
        initialRoomName={room.get('name')}
        initialRoomId={roomId}
        isRoomRostered={room.hasRoster()}
      />
      <ImportQuizToWorkspaceDialog
        parentId={parentId}
        workspaceId={workspaceId}
        open={importQuizToWorkspaceDialogState.isOpen}
        onClose={() => importQuizToWorkspaceDialogState.setIsOpen(false)}
      />
      <AddQuizDialog
        isOpen={isAddQuizDialogOpen}
        importQuiz={handleImportQuiz}
        onClose={onClose}
        generateQuestions={handleGenerateQuestions}
        importFromText={handleImportFromText}
        createBlank={handleCreateBlank}
      />
      {/* 
      If the user is a pro user, then the export answer key dialog will be shown in the shared library page.
      */}
      {selectedQuizId && selectedWorkspaceId && (
        <Dialog
          id="workspace-answer-key-export-dialog"
          open={isAnswerKeyDialogOpen}
          onClose={handleCloseAnswerKeyDialog}
          size="default"
          title={translate('Export Answer Key')}
        >
          {isAnswerKeyDialogOpen && (
            <QuizAnswerKeyExportModal
              key={`answer-key-${selectedQuizId}-${isAnswerKeyDialogOpen}`}
              quizId={selectedQuizId}
              workspaceId={selectedWorkspaceId}
              onExportingChange={setIsExportingAnswerKey}
            />
          )}
        </Dialog>
      )}
    </div>
  );
}
